import { useAddTagMutation, useUpdateTagMutation } from 'features/documents/documents';
import { useActiveOrganisation } from 'hooks/useActiveOrganisation';
import { useEffect, useMemo } from 'react';
import { useImmer } from 'use-immer';

import { useGetBaseVariant } from '../../documents_v2/hooks/useGetBaseVariant';

/**
 * Shared state and save logic for tag modals: seeds the form from the loaded
 * tag, tracks dirty state and submits create/update requests.
 *
 * @param {number} documentId
 * @param {import('models/documents.models').Tag | undefined} tag loaded tag used to seed the form
 * @param {boolean} mergeTagValueIntoBase merge tagValue edits into the base variant item on update
 */
export function useTagFormState({ documentId, tag, mergeTagValueIntoBase = false }) {
    const activeOrganisation = useActiveOrganisation();
    const baseVariant = useGetBaseVariant();
    const [updateTag] = useUpdateTagMutation();
    const [addTag] = useAddTagMutation();

    const [state, setState] = useImmer({
        formData: {
            tagItems: [],
        },
        saveInProgress: false,
        showAllVariants: false,
    });

    // Re-seed form data only when a different tag is loaded, so that cache
    // invalidations/refetches of the same tag do not discard unsaved edits.
    useEffect(() => {
        setState((draft) => {
            if (draft.formData?.id !== tag?.id) {
                draft.formData = tag;
            }
        });
    }, [setState, tag]);

    const initialItems = useMemo(() => (tag ? normalizeTagItems(tag.tagItems) : undefined), [tag]);

    const dirty = useMemo(() => {
        if (!initialItems || !state.formData) {
            return false;
        }

        return normalizeTagItems(state.formData.tagItems) !== initialItems;
    }, [state.formData, initialItems]);

    async function handleFormSubmit(event) {
        event.preventDefault();
        event.stopPropagation();

        setState((draft) => {
            draft.saveInProgress = true;
        });

        try {
            // Create new Tag for Document Tag?
            if (documentId && !state.formData.documentId) {
                const createdTag = await addTag({
                    organisationId: activeOrganisation,
                    body: {
                        name: state.formData.name,
                        isOverride: true,
                        document: documentId,
                        tagItems: mapTagItemsForApi(state.formData.tagItems),
                    },
                }).unwrap();

                setState((draft) => {
                    draft.formData = createdTag;
                });

                return;
            }

            let tagItemsForSave = state.formData.tagItems;

            if (mergeTagValueIntoBase && !state.showAllVariants) {
                const baseItemIndex = tagItemsForSave?.findIndex((item) => item.documentVariantId === baseVariant?.id);

                if (baseItemIndex !== undefined && baseItemIndex !== -1) {
                    const tagValue = state.formData.tagValue ?? {};

                    tagItemsForSave = tagItemsForSave.map((item, index) =>
                        // tagValue provides the tag-level defaults; the item's own
                        // edits must win, mirroring `defaultItem` in useTagFormHandlers.
                        index === baseItemIndex ? { ...tagValue, ...item } : item,
                    );

                    setState((draft) => {
                        draft.formData.tagItems = tagItemsForSave;
                    });
                }
            }

            await updateTag({
                organisationId: activeOrganisation,
                id: state.formData.id,
                body: {
                    name: state.formData.name,
                    description: state.formData.description,
                    tagValue: state.formData.tagValue,
                    tagItems: mapTagItemsForApi(tagItemsForSave),
                },
            }).unwrap();
        } finally {
            setState((draft) => {
                draft.saveInProgress = false;
            });
        }
    }

    return { state, setState, dirty, handleFormSubmit };
}

/**
 * Normalizes tag items for comparison by reducing them to the API-relevant
 * fields in a stable key order, with empty strings treated as null. This makes
 * backend items (which carry extra keys such as `id`, `tag` and a
 * `documentVariant` IRI, and a different key order) compare equal to their form
 * counterparts once saved, so `dirty` clears correctly.
 */
function normalizeTagItems(tagItems) {
    return JSON.stringify(mapTagItemsForApi(tagItems), (_key, value) => (value === '' ? null : value));
}

function mapTagItemsForApi(tagItems) {
    return tagItems?.map((tagItem) => ({
        documentVariant: tagItem.documentVariantId,
        text: tagItem.text,
        url: tagItem.url,
        tooltip: tagItem.tooltip,
        crossReference: tagItem.crossReference,
    }));
}
